<script>
import api from '@/api'
import { alertSuccess, alertConfirm, alertError } from '@/helpers/swal'
import pagination from '@/components/pagination.vue'

export default {
  data() {
    return {
      loading: true,
      pagetitle: 'Scheduled Maintenance',
      devices: [],
      totalItems: 0,
      totalPages: 1,
      currentPage: 0,
      perPage: 10,
      baseUrl: `${window.apiUrl}/maintenance/device/list`,
      exportBtnDisabled: false,
    }
  },
  components: {
    pagination,
  },
  mounted() {
    this.getDevices()
  },
  methods: {
    getDevices() {
      const params = {
        page: this.currentPage,
        size: this.perPage,
      }
      api
        .get(this.baseUrl, { params })
        .then((response) => {
          this.devices = response.data?.devices || []
          this.totalItems = response.data.totalItems
          this.totalPages = response.data.totalPages
          this.currentPage = response.data.currentPage
        })
        .finally(() => {
          this.loading = false
        })
    },

    deleteDevice(id) {
      alertConfirm('You want to delete this device').then((result) => {
        if (result.value) {
          api.post(`/maintenance/device/delete/${id}`).then(() => {
            alertSuccess('Device deleted successfully')
            this.getDevices()
          })
        }
      })
    },

    exportData() {
      if (!this.devices.length) {
        alertError('There is no data to export')
        return
      }
      this.exportBtnDisabled = true
      api
        .get('/maintenance/device/export')
        .then((response) => {
          let csvString = response.data
          this.exportCSVStringToFile('Maintenance.csv', csvString)
        })
        .finally(() => {
          this.exportBtnDisabled = false
        })
    },

    exportCSVStringToFile(filename, csvString) {
      const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' }) // Add BOM for Excel compatibility
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    updatePage(newPage) {
      this.loading = true
      this.currentPage = newPage - 1
      this.getDevices()
    },

    showActionColumn() {
      return this.authUserHasPermission('device_maintenance_edit') || this.authUserHasPermission('device_maintenance_delete')
    },
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle }}
  </Teleport>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header">
          <div class="row g-3 align-items-center">
            <div class="col-lg-4 col-md-6">
              <!-- <div class="input-group">
                <input type="search" class="form-control" placeholder="Search here" />
                <span class="input-group-text cursor-pointer">
                  <i class="bx bx-search"></i>
                </span>
              </div> -->
            </div>
            <div class="col-md-6 col-lg">
              <div class="d-flex justify-content-end">
                <button v-if="authUserHasPermission('device_maintenance_export')" class="btn btn-light me-2 d-flex align-items-center gap-1" @click="exportData()" :disabled="exportBtnDisabled">
                  <i v-if="exportBtnDisabled" class="bx bx-loader bx-spin"></i>
                  <i v-else class="bx bx-export"></i>
                  Export
                </button>
                <router-link v-if="authUserHasPermission('device_maintenance_add')" to="/maintenance/schedules/create" class="btn btn-primary d-flex align-items-center gap-1">
                  <i class="bx bx-plus"></i>
                  Add New
                </router-link>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Host</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Remarks</th>
                      <th v-if="showActionColumn()">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="loading">
                      <tr v-for="n in 5" :key="n">
                        <td v-for="m in showActionColumn() ? 5 : 4" :key="m" class="placeholder-glow">
                          <span class="placeholder col-12"></span>
                        </td>
                      </tr>
                    </template>
                    <template v-else-if="devices.length">
                      <tr v-for="(device, index) in devices" :key="index">
                        <td class="text-uppercase">{{ device.host }}</td>
                        <td>{{ device.startTime }}</td>
                        <td>{{ device.endTime }}</td>
                        <td>{{ device.remarks }}</td>
                        <td v-if="showActionColumn()">
                          <div class="d-flex gap-2">
                            <router-link
                              v-if="authUserHasPermission('device_maintenance_edit')"
                              :to="'/maintenance/schedules/' + device.id + '/edit'"
                              class="btn btn-primary bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Edit"
                            >
                              <eva-icon name="edit" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </router-link>
                            <button
                              v-if="authUserHasPermission('device_maintenance_delete')"
                              type="button"
                              class="btn btn-danger bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Delete"
                              @click="deleteDevice(device.id)"
                            >
                              <eva-icon name="trash-2" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td colspan="5" class="text-center">No devices found</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div v-if="!loading && devices.length" class="card-footer">
          <pagination :baseUrl="baseUrl" :totalItems="totalItems" :perPage="perPage" :currentPage="currentPage + 1" @page-changed="updatePage" />
        </div>
      </div>
    </div>
  </div>
</template>
